import { Box, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { ArrayPath, FieldArrayWithId, useWatch } from 'react-hook-form';

import { AccordionContext } from '@/components/Accordion/Accordion.context';
import { Button } from '@/components/form/baseInputs/Button';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCardRepeater } from '@/components/form/formAwareInputs/FormAwareCardRepeater/FormAwareCardRepeater';
import { Share04Icon } from '@/components/icons/Share04Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useAISuggestionsEnabled } from '@/modules/aiSuggestions/hooks/useAISuggestionsEnabled';
import { useIsCurrentBeneficiaryEntity } from '@/modules/beneficiaries/beneficiaries.hooks';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import { useEditEntitySplitScreenModalContext } from '../EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { CharitableBeneficiaryFormBody } from './CharitableBeneficiaryFormBody';
import {
  defaultBeneficiary,
  defaultCharitableBeneficiary,
} from './EntityBeneficiariesSubform.defaults';
import {
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesFormBodyProps,
  EntityBeneficiariesFormSecondNamespace,
  EntityBeneficiariesSubformFields,
  EntityBeneficiariesSubformProps,
  EntityBeneficiariesSubformSubformPath,
} from './EntityBeneficiariesSubform.types';
import { isCharitableBeneficiarySubform } from './EntityBeneficiariesSubform.utils';
import { EntityBeneficiaryFormBody } from './EntityBeneficiaryFormBody';
import { useBeneficiariesFormOptionsQuery } from './graphql/Beneficiaries.generated';
import { useGetBeneficiaryOptions } from './useGetBeneficiaryOptions';

export function UI(props: EntityBeneficiariesSubformProps) {
  const {
    householdId,
    namespace,
    variant,
    entityType,
    suggestionKindOverride,
  } = props;
  const { entityId } = useEntityDetailsContext();
  const { control } = useFormContext<EntityBeneficiariesSubformFields>();
  const { createErrorFeedback } = useFeedback();
  const shouldGroupDuplicates = useAISuggestionsEnabled(entityType);
  const { duplicatesRefs } = useEditEntitySplitScreenModalContext();
  const isCurrentBeneficiaryEntity = useIsCurrentBeneficiaryEntity();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();

  const isCharitableSubform = isCharitableBeneficiarySubform(variant);

  let secondNamespace: EntityBeneficiariesFormSecondNamespace =
    EntityBeneficiariesFormSecondNamespace.Beneficiaries;

  if (namespace === 'lifetimeBeneficiariesSubform') {
    secondNamespace =
      EntityBeneficiariesFormSecondNamespace.LifetimeBeneficiaries;
  }

  if (namespace === 'remainderBeneficiariesSubform') {
    secondNamespace =
      EntityBeneficiariesFormSecondNamespace.RemainderBeneficiaries;
  }

  if (namespace === 'leadBeneficiarySubform') {
    secondNamespace = EntityBeneficiariesFormSecondNamespace.LeadBeneficiary;
  }

  if (namespace === 'incomeBeneficiariesSubform') {
    secondNamespace =
      EntityBeneficiariesFormSecondNamespace.IncomeBeneficiaries;
  }

  const namespacePath =
    `${namespace}.${secondNamespace}` as const satisfies EntityBeneficiariesSubformSubformPath;

  const subformValues = useWatch({
    name: namespacePath,
    control,
  });

  const beneficiariesAsList: EntityBeneficiariesFormBeneficiary[] | undefined =
    (() => {
      if (!subformValues) return undefined;

      if (Array.isArray(subformValues)) {
        return subformValues;
      }

      return [subformValues];
    })();

  const {
    data,
    loading: loadingFormOptions,
    error,
  } = useBeneficiariesFormOptionsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    skip: !householdId,
    variables: {
      whereAdvisorClients: {
        id: householdId,
      },
      whereEntities: {
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
      },
    },
    onError: createErrorFeedback(
      "We weren't able to initialize this form. Please refresh the page and try again."
    ),
  });

  // Only allow the "Surviving Spouse" option for subtrust (testamentary entity) beneficiary forms
  // in the "Access after death" section (first death), NOT in "Access after both deaths".
  // When both grantors have died, there is no surviving spouse, so the option doesn't make sense.
  const allowSurvivingSpouseOption =
    variant === 'testamentaryEntity' &&
    namespace !== 'beneficiariesAfterBothGrantorsDie';

  const { options: beneficiaryOptions } = useGetBeneficiaryOptions({
    currentEntityId: entityId,
    queryData: data,
    subformValues: beneficiariesAsList,
    isTwoClientHousehold: isTwoClientHousehold ?? false,
    allowSurvivingSpouseOption,
  });

  useReportError('failed to fetch beneficiaries form options', error);

  const [closedAccordionIds, setClosedAccordionIds] = useState<string[]>([]);

  const handleAppend = (
    fields: FieldArrayWithId<
      EntityBeneficiariesSubformFields,
      ArrayPath<EntityBeneficiariesSubformFields>,
      '_id'
    >[]
  ) => {
    const fieldIds = fields.map((field) => field._id);
    const allExceptLast = fieldIds.slice(0, -1);
    setClosedAccordionIds(allExceptLast);
  };

  return (
    <FormFieldsDisabledProvider isDisabled={loadingFormOptions}>
      {variant !== 'testamentaryEntity' && (
        <Callout severity="info-high">
          <Stack spacing={0.5}>
            <Typography variant="title-xs">How is this data used?</Typography>
            <Typography variant="body-xs">
              Both current beneficiaries & disposition details are used to
              populate our beneficiary reporting dashboard which aggregates and
              illustrates all current and projected access for each beneficiary.
            </Typography>
            <Typography variant="body-xs">
              <ContextualHelpTrigger
                display="Learn more"
                contextualHelpId="beneficiaryReporting"
                endIcon={<Share04Icon size={14} />}
              />
            </Typography>
          </Stack>
        </Callout>
      )}
      <Grid
        pt={variant !== 'testamentaryEntity' ? 2 : 0}
        container
        spacing={2}
        columns={12}
      >
        <Grid item sm={12}>
          {!isCharitableSubform && (
            <AccordionContext.Provider value={{ closedAccordionIds }}>
              <FormAwareCardRepeater<EntityBeneficiariesSubformFields>
                allowEmpty={true}
                name={namespacePath}
                showRemove={false}
                removeButtonText="Remove beneficiary"
                shouldInit={true}
                emptyValue={defaultBeneficiary()}
                control={control}
                addAnotherItemText={
                  isCurrentBeneficiaryEntity
                    ? 'Add individual'
                    : 'Add additional beneficiary'
                }
                onAppend={(fields) => handleAppend(fields)}
                render={(i, { remove }, _id, additionalItemRendererProps) => (
                  <EntityBeneficiaryFormBody
                    key={`beneficiary-${namespace}-${i}`}
                    control={control}
                    beneficiaryOptions={beneficiaryOptions}
                    namespace={namespace}
                    subformValues={beneficiariesAsList}
                    secondNamespace={secondNamespace}
                    householdId={householdId}
                    variant={variant}
                    i={i}
                    additionalItemRendererProps={additionalItemRendererProps}
                    splitScreen
                    remove={remove}
                    listItemId={_id}
                    suggestionKindOverride={suggestionKindOverride}
                  />
                )}
                idField="beneficiaryId"
                shouldGroupDuplicates={shouldGroupDuplicates}
                duplicatesRef={
                  duplicatesRefs[
                    namespacePath as AISuggestionsMatcherArrayField
                  ]
                }
              />
            </AccordionContext.Provider>
          )}
          {isCharitableSubform &&
            namespacePath === 'leadBeneficiarySubform.leadBeneficiary' && (
              <Box>
                <CharitableBeneficiaryFormBody
                  control={control}
                  beneficiaryOptions={beneficiaryOptions}
                  householdId={householdId}
                  namespace={namespacePath}
                  splitScreen
                />
              </Box>
            )}
          {isCharitableSubform &&
            namespacePath ===
              'incomeBeneficiariesSubform.incomeBeneficiaries' && (
              <>
                <FormLayoutRow>
                  <FormLayoutItem width={12}>
                    <FormAwareButtonGroup<EntityBeneficiariesSubformFields>
                      label=""
                      fieldName="incomeBeneficiariesSubform.charitableBeneficiaryType"
                      control={control}
                      options={[
                        {
                          display: 'Annuity',
                          value: 'annuity',
                        },
                        {
                          display: 'Unitrust',
                          value: 'unitrust',
                        },
                      ]}
                    />
                  </FormLayoutItem>
                </FormLayoutRow>
                <AccordionContext.Provider value={{ closedAccordionIds }}>
                  <FormAwareCardRepeater<EntityBeneficiariesSubformFields>
                    allowEmpty
                    name={namespacePath}
                    showRemove
                    removeButtonText="Remove income beneficiary"
                    shouldInit
                    emptyValue={defaultCharitableBeneficiary()}
                    control={control}
                    addAnotherItemText="Add income beneficiary"
                    onAppend={(fields) => handleAppend(fields)}
                    render={(i, { remove }) => (
                      <CRTIncomeBeneficiaryCard
                        key={`income-beneficiary-${i}`}
                        control={control}
                        beneficiaryOptions={beneficiaryOptions}
                        householdId={householdId}
                        namespacePath={namespacePath}
                        index={i}
                        onRemove={() => remove(i)}
                      />
                    )}
                    idField="beneficiaryId"
                    shouldGroupDuplicates={shouldGroupDuplicates}
                    duplicatesRef={
                      duplicatesRefs[
                        namespacePath as AISuggestionsMatcherArrayField
                      ]
                    }
                  />
                </AccordionContext.Provider>
              </>
            )}
        </Grid>
      </Grid>
    </FormFieldsDisabledProvider>
  );
}

interface CRTIncomeBeneficiaryCardProps {
  control: EntityBeneficiariesFormBodyProps['control'];
  beneficiaryOptions: EntityBeneficiariesFormBodyProps['beneficiaryOptions'];
  householdId: string;
  namespacePath: 'incomeBeneficiariesSubform.incomeBeneficiaries';
  index: number;
  onRemove: () => void;
}

function CRTIncomeBeneficiaryCard({
  control,
  beneficiaryOptions,
  householdId,
  namespacePath,
  index,
  onRemove,
}: CRTIncomeBeneficiaryCardProps) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  return (
    <Box p={2}>
      <CharitableBeneficiaryFormBody
        control={control}
        beneficiaryOptions={beneficiaryOptions}
        householdId={householdId}
        namespace={namespacePath}
        index={index}
        splitScreen
      />
      <Stack direction="row" justifyContent="flex-end" pt={1}>
        <Button
          disabled={disabledFromContext}
          variant="destructive-transparent"
          size="sm"
          onClick={onRemove}
          startIcon={Trash01Icon}
          data-testid="button-remove-repeater-input-item"
        >
          Remove beneficiary
        </Button>
      </Stack>
    </Box>
  );
}
